<template>
  <component
    :is="tag"
    :disabled="loading"
    :style="buttonStyle"
    :to="to"
    :href="href === '' ? undefined : href"
    :target="target"
    tabindex="0"
    @click="handleClick"
    @mousedown.space.prevent
    @mouseenter="hover = true"
    @mouseleave="hover = false"
    @focus="focus = true"
    @blur="focus = false"
  >
    <c-icon
      v-if="icon !== '' || loading"
      :style="fab ? {} : { marginRight: '.6rem' }"
      :size="iconSize"
      :type="localIcon"
      :spin="loading"
      :color="fontColor"
    />
    <slot />
  </component>
</template>
<script>
import CIcon from 'shared/components/CIcon.vue';

export default {
  name: 'CButton',
  props: {
    outlined: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'default',
    },
    icon: {
      type: String,
      default: '',
    },
    to: {
      type: [Object, String],
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '',
    },
    stretch: {
      type: Boolean,
      default: false,
    },
    hasShadow: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fab: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'default',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: { CIcon },
  data() {
    return {
      hover: false,
      focus: false,
    };
  },
  emits: ['click'],
  computed: {
    height() {
      if (this.tag === 'a') {
        return 'unset';
      }
      return '100%';
    },
    iconSize() {
      if (this.size === 'xs') {
        return '14';
      }

      if (this.size === 'small') {
        return '14';
      }

      if (this.size === 'large') {
        return '16';
      }

      return '18';
    },
    localIcon() {
      if (this.loading) {
        return 'loading';
      }
      return this.icon;
    },
    buttonStyle() {
      const middlewares = [
        this.inker,
        this.stretcher,
        this.shadow,
        this.disabler,
        this.resizer,
        this.boxSizer,
        this.makeBorderRadius,
      ];
      let res = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
        padding: '.8rem 1.4rem',
        fontSize: '1.6rem',
        lineHeight: '1',
        outline: 'none',
        color: this.$colors.grey.darken4,
      };
      middlewares.forEach((m) => {
        res = m(res);
      });
      return res;
    },
    tag() {
      if (this.to !== '') {
        return 'router-link';
      }

      if (this.href !== '') {
        return 'a';
      }

      return 'button';
    },
    fontColor() {
      if (this.color === 'primary') {
        if (this.outlined) {
          return this.$colors.red.base;
        }
        return 'white';
      }

      if (this.color === 'superlight') {
        return this.$colors.grey.lighten2;
      }

      if (this.color === 'light') {
        return this.$colors.grey.base;
      }

      if (this.disabled) {
        return this.$colors.grey.base;
      }

      return this.$colors.grey.darken4;
    },
  },
  methods: {
    handleClick(event) {
      if (this.disabled) {
        return;
      }
      this.$emit('click', event);
    },
    makeBorderRadius(style) {
      if (this.rounded) {
        return {
          borderRadius: '50%',
          ...style,
        };
      }
      return {
        borderRadius: '.4rem',
        ...style,
      };
    },
    boxSizer(style) {
      if (this.tag === 'a') {
        return {
          ...style,
          boxSizing: 'border-box',
        };
      }

      return style;
    },
    resizer(style) {
      if (this.fab) {
        if (this.size === 'xs') {
          return {
            ...style,
            width: '2.4rem',
            maxHeight: '2.4rem',
            height: this.height,
          };
        }
        if (this.size === 'small') {
          return {
            ...style,
            width: '2.8rem',
            maxHeight: '2.8rem',
            height: this.height,
          };
        }
        return {
          ...style,
          width: '3.2rem',
          maxHeight: '3.2rem',
          height: this.height,
        };
      }
      if (this.size === 'xs') {
        return {
          ...style,
          maxHeight: '2.4rem',
          height: this.height,
        };
      }
      if (this.size === 'small') {
        return {
          ...style,
          maxHeight: '2.8rem',
          height: this.height,
        };
      }

      if (this.size === 'large') {
        return {
          ...style,
          maxHeight: '3.6rem',
          height: this.height,
        };
      }

      return {
        ...style,
        maxHeight: '3.2rem',
        height: this.height,
      };
    },
    disabler(style) {
      if (this.disabled) {
        return {
          ...style,
          cursor: 'not-allowed',
        };
      }
      if (this.loading) {
        return {
          ...style,
          cursor: 'normal',
        };
      }

      return style;
    },
    shadow(style) {
      if (this.hasShadow) {
        return { ...style };
      }

      return style;
    },
    stretcher(style) {
      if (this.stretch) {
        return {
          ...style,
          width: '100%',
        };
      }

      return style;
    },
    inker(style) {
      switch (this.color) {
        case 'primary': {
          if (this.outlined) {
            if (this.hover && !this.disabled) {
              return {
                ...style,
                color: this.fontColor,
                backgroundColor: this.$colors.red.lighten5,
                boxShadow: 'rgb(15 15 15 / 10%) 0px 1px 2px, rgb(235 87 87 / 30%) 0px 0px 0px 1px inset',
              };
            }
            return {
              ...style,
              color: this.fontColor,
              backgroundColor: this.$colors.red.lighten6,
              boxShadow: 'rgb(16 15 15 / 10%) 0px 1px 2px, rgb(235 87 87 / 30%) 0px 0px 0px 1px inset',
            };
          }
          if (this.hover && !this.disabled) {
            return {
              ...style,
              color: this.fontColor,
              backgroundColor: this.$colors.red.darken1,
            };
          }
          if (this.loading) {
            return {
              ...style,
              color: this.fontColor,
              backgroundColor: this.$colors.red.lighten2,
              boxShadow: 'rgb(15 15 15 / 10%) 0px 1px 2px, rgb(235 87 87 / 30%) 0px 0px 0px 1px inset',
            };
          }
          return {
            ...style,
            color: this.fontColor,
            backgroundColor: this.$colors.red.base,
          };
        }
        case 'light':
        case 'superlight':
        case 'default': {
          if (this.outlined) {
            if (this.hover && !this.disabled) {
              return {
                ...style,
                color: this.fontColor,
                backgroundColor: this.$colors.greyHex.lighten6,
                border: '1px solid rgba(15, 15, 15, 0.15)',
                boxShadow: 'rgb(15 15 15 / 5%) 0px 1px 2px',
              };
            }
            return {
              ...style,
              color: this.fontColor,
              backgroundColor: 'transparent',
              border: '1px solid rgba(15, 15, 15, 0.15)',
              boxShadow: 'rgb(15 15 15 / 5%) 0px 1px 2px',
            };
          }
          if (this.hover && !this.disabled) {
            return {
              ...style,
              color: this.fontColor,
              backgroundColor: this.$colors.greyHex.lighten4,
            };
          }
          return {
            ...style,
            color: this.fontColor,
            backgroundColor: 'transparent',
          };
        }
        default:
          return {};
      }
    },
  },
};
</script>
<style type="text/scss">

</style>
